// src/pages/ChapterResources.tsx
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
    Box, Typography, Tab, Tabs, Paper, CircularProgress,
    IconButton, Button, Chip, Stack, Tooltip,
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Refresh as RefreshIcon,
    MenuBook as ChapterIcon,
    PlayCircleOutline as IntroIcon,
    Quiz as McqIcon,
    LooksOne as OneMarkIcon,
    LooksTwo as TwoMarksIcon,
    Looks3 as ThreeMarksIcon,
    Looks4 as FourMarksIcon,
    Looks5 as FiveMarksIcon,
    PictureAsPdf as PdfIcon,
} from '@mui/icons-material';
import { keyframes } from '@mui/system';

import { getChapterQuestions } from '../services/chapterService';
import { ChapterResourcesResponse } from '../models/ChapterResourcesDomain';

import ChapterIntroTab from './resources/ChapterIntroTab';
import ChapterMcqTab from './resources/ChapterMcqTab';
import ChapterOneMarkTab from './resources/ChapterOneMarkTab';
import ChapterLongQuestionsTab from './resources/ChapterLongQuestionsTab';

const PRIMARY = '#174143';
const ACCENT = '#3d9ea4';

const fadeUp = keyframes`
  from { opacity:0; transform:translateY(12px); }
  to   { opacity:1; transform:translateY(0); }
`;

const TAB_CONFIG = [
    { label: 'Intro', icon: <IntroIcon sx={{ fontSize: 18 }} />, key: 'introduction' },
    { label: 'MCQs', icon: <McqIcon sx={{ fontSize: 18 }} />, key: 'mcq' },
    { label: '1 Mark', icon: <OneMarkIcon sx={{ fontSize: 18 }} />, key: 'oneMark' },
    { label: '2 Marks', icon: <TwoMarksIcon sx={{ fontSize: 18 }} />, key: 'twoMarks' },
    { label: '3 Marks', icon: <ThreeMarksIcon sx={{ fontSize: 18 }} />, key: 'threeMarks' },
    { label: '4 Marks', icon: <FourMarksIcon sx={{ fontSize: 18 }} />, key: 'fourMarks' },
    { label: '5 Marks', icon: <FiveMarksIcon sx={{ fontSize: 18 }} />, key: 'fiveMarks' },
    { label: 'PDF', icon: <PdfIcon sx={{ fontSize: 18 }} />, key: 'pdf' },
];

function countItems(data: ChapterResourcesResponse | null, key: string): number {
    if (!data) return 0;
    const val = (data as any)[key];
    if (!val) return 0;
    if (Array.isArray(val)) return val.length;
    return 1; // introduction = single object
}

export default function ChapterResources() {
    const { id } = useParams();
    const chapterId = Number(id);
    const navigate = useNavigate();
    const location = useLocation();

    const stateName = (location.state as any)?.name || 'Chapter Resources';

    const [data, setData] = useState<ChapterResourcesResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [fetchError, setFetchError] = useState(false);

    // Initialise the active tab from the URL hash so navigating away (e.g. to the
    // CreateLongQuestionPage) and back keeps the user on the same tab.
    const tabFromHash = () => {
        const h = (window.location.hash || '').replace(/^#/, '');
        const idx = TAB_CONFIG.findIndex(t => t.key === h);
        return idx >= 0 ? idx : 0;
    };
    const [tabIndex, setTabIndex] = useState<number>(tabFromHash);

    // Keep the URL hash in sync with the selected tab.
    useEffect(() => {
        const key = TAB_CONFIG[tabIndex]?.key;
        if (key) {
            const newHash = `#${key}`;
            if (window.location.hash !== newHash) {
                window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}${newHash}`);
            }
        }
    }, [tabIndex]);

    // React to back/forward navigation that changes the hash.
    useEffect(() => {
        const handler = () => setTabIndex(tabFromHash());
        window.addEventListener('hashchange', handler);
        return () => window.removeEventListener('hashchange', handler);
    }, []);

    const refreshData = async (showSpinner = false) => {
        if (!chapterId) return;
        if (showSpinner) setRefreshing(true);
        try {
            const res = await getChapterQuestions(chapterId);
            setData(res);
            setFetchError(false);
        } catch (err) {
            console.error('Failed to refresh chapter resources', err);
            setFetchError(true);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    useEffect(() => {
        setLoading(true);
        refreshData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chapterId]);

    // ── Full-screen loader ─────────────────────────────────
    if (loading && !data) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '60vh', gap: 2 }}>
                <CircularProgress sx={{ color: PRIMARY }} size={40} />
                <Typography variant="body2" color="text.secondary">Loading chapter resources…</Typography>
            </Box>
        );
    }

    // ── Error fallback ──────────────────────────────────────
    if (fetchError && !data) {
        return (
            <Box sx={{ p: 6, textAlign: 'center' }}>
                <ChapterIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 2 }} />
                <Typography variant="h6" color="error" gutterBottom>Failed to load resources</Typography>
                <Button variant="contained" onClick={() => refreshData(true)}
                    sx={{ background: `linear-gradient(135deg, ${PRIMARY}, ${ACCENT})`, borderRadius: 2, fontWeight: 700 }}>
                    Retry
                </Button>
            </Box>
        );
    }

    return (
        <Box sx={{ minHeight: '100vh', background: '#f5f6fa', pb: 6 }}>

            {/* ── Sticky Header ── */}
            <Paper
                elevation={0}
                sx={{
                    position: 'sticky', top: 0, zIndex: 10,
                    borderBottom: '1px solid', borderColor: 'divider',
                    background: '#fff',
                }}
            >
                {/* Title bar */}
                <Box
                    sx={{
                        px: { xs: 2, md: 3 }, pt: 2, pb: 1,
                        display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap',
                    }}
                >
                    <Tooltip title="Back to Chapters">
                        <IconButton
                            onClick={() => navigate('/chapters')}
                            sx={{
                                border: '1px solid', borderColor: 'divider', borderRadius: 2,
                                color: PRIMARY, '&:hover': { background: 'rgba(23,65,67,0.06)' },
                            }}
                        >
                            <ArrowBackIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>

                    {/* Icon badge */}
                    <Box sx={{
                        width: 40, height: 40, borderRadius: 2,
                        background: `linear-gradient(135deg, ${PRIMARY}, ${ACCENT})`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}>
                        <ChapterIcon sx={{ fontSize: 20, color: '#fff' }} />
                    </Box>

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="h6" fontWeight={800} color={PRIMARY} noWrap sx={{ letterSpacing: -0.3 }}>
                            {stateName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Resource Management · Chapter ID: {chapterId}
                        </Typography>
                    </Box>

                    <Stack direction="row" spacing={1} alignItems="center">
                        <Tooltip title="Refresh resources">
                            <IconButton
                                size="small"
                                onClick={() => refreshData(true)}
                                disabled={refreshing}
                                sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}
                            >
                                {refreshing
                                    ? <CircularProgress size={16} sx={{ color: PRIMARY }} />
                                    : <RefreshIcon fontSize="small" />}
                            </IconButton>
                        </Tooltip>
                    </Stack>
                </Box>

                {/* Tab row */}
                <Tabs
                    value={tabIndex}
                    onChange={(_, v) => setTabIndex(v)}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{
                        px: { xs: 2, md: 3 },
                        '& .MuiTab-root': {
                            fontWeight: 600,
                            fontSize: '0.8rem',
                            textTransform: 'none',
                            minHeight: 44,
                            gap: 0.75,
                            color: 'text.secondary',
                            '&.Mui-selected': { color: PRIMARY, fontWeight: 800 },
                        },
                        '& .MuiTabs-indicator': { background: `linear-gradient(90deg, ${PRIMARY}, ${ACCENT})`, height: 3, borderRadius: 2 },
                    }}
                >
                    {TAB_CONFIG.map((tab, idx) => {
                        const count = countItems(data, tab.key);
                        return (
                            <Tab
                                key={tab.key}
                                icon={tab.icon}
                                iconPosition="start"
                                label={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                        {tab.label}
                                        {count > 0 && (
                                            <Chip
                                                label={count}
                                                size="small"
                                                sx={{
                                                    height: 18, fontSize: '0.65rem', fontWeight: 800,
                                                    background: tabIndex === idx
                                                        ? `linear-gradient(135deg, ${PRIMARY}, ${ACCENT})`
                                                        : 'rgba(0,0,0,0.08)',
                                                    color: tabIndex === idx ? '#fff' : 'text.secondary',
                                                    '& .MuiChip-label': { px: 0.75 },
                                                }}
                                            />
                                        )}
                                    </Box>
                                }
                            />
                        );
                    })}
                </Tabs>
            </Paper>

            {/* ── Tab Content ── */}
            <Box sx={{ px: { xs: 2, md: 3 }, mt: 3, animation: `${fadeUp} 0.35s ease-out` }}>
                {tabIndex === 0 && (
                    <ChapterIntroTab chapterId={chapterId} data={data?.introduction || null} refreshData={refreshData} />
                )}
                {tabIndex === 1 && (
                    <ChapterMcqTab chapterId={chapterId} data={data?.mcq ?? []} refreshData={refreshData} />
                )}
                {tabIndex === 2 && (
                    <ChapterOneMarkTab chapterId={chapterId} data={data?.oneMark ?? []} refreshData={refreshData} />
                )}
                {tabIndex === 3 && (
                    <ChapterLongQuestionsTab chapterId={chapterId} data={data?.twoMarks ?? []} refreshData={refreshData} marks={2} title="2 Mark Questions" />
                )}
                {tabIndex === 4 && (
                    <ChapterLongQuestionsTab chapterId={chapterId} data={data?.threeMarks ?? []} refreshData={refreshData} marks={3} title="3 Mark Questions" />
                )}
                {tabIndex === 5 && (
                    <ChapterLongQuestionsTab chapterId={chapterId} data={data?.fourMarks ?? []} refreshData={refreshData} marks={4} title="4 Mark Questions" />
                )}
                {tabIndex === 6 && (
                    <ChapterLongQuestionsTab chapterId={chapterId} data={data?.fiveMarks ?? []} refreshData={refreshData} marks={5} title="5 Mark Questions" />
                )}
                {tabIndex === 7 && (
                    <ChapterLongQuestionsTab chapterId={chapterId} data={data?.pdf ?? []} refreshData={refreshData} marks={6} title="PDF Documents" />
                )}
            </Box>
        </Box>
    );
}
